import React, { useEffect, useState } from 'react';
import {
  Table,
  Badge,
  Spin,
  message,
  Dropdown,
  Menu,
  Row,
  Col,
  Tooltip,
  Popover,
} from 'antd';
import { Button, Icon, Colors } from '@cognite/cogs.js';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  startConvertFileToSvgJob,
  UploadJobState,
} from 'modules/fileContextualization/uploadJobs';
import { RootState } from 'reducers/index';
import {
  linkFileWithAssetsFromAnnotations,
  selectAnnotationsForSource,
  selectAnnotations,
} from 'modules/annotations';
import { FilesMetadata } from '@cognite/sdk';
import { ClickParam } from 'antd/lib/menu';
import { dataKitItemsSelector } from 'modules/selection';
import { Result } from 'modules/sdk-builder/types';
import { canEditFiles } from 'utils/PermissionUtils';
import queryString from 'query-string';
import {
  getPnIdAnnotationCategories,
  selectAnnotationColor,
} from 'components/PnIDPreview/AnnotatedPnIDOverview';
import { startPnidParsingPipeline } from 'modules/fileContextualization/pnidPipeline';
import { ParsingJobState } from 'modules/fileContextualization/parsingJobs';
import LoadResources from './LoadResources';

export default function PnidParsing() {
  const { tenant, assetsDataKitId, filesDataKitId, optionsId } = useParams<{
    tenant: string;
    filesDataKitId: string;
    assetsDataKitId: string;
    optionsId: string;
  }>();

  const history = useHistory();
  const { search } = history.location;

  const { page } = queryString.parse(search, { parseNumbers: true });

  const dispatch = useDispatch();

  const annotationBySourceMap = useSelector(selectAnnotationsForSource);
  const annotationsMap = useSelector(selectAnnotations);

  const [assetDataKit, fileDataKit] = useSelector((state: RootState) => [
    state.selection.items[assetsDataKitId],
    state.selection.items[filesDataKitId],
  ]);

  useEffect(() => {
    if (!assetDataKit || !fileDataKit) {
      message.error('Invalid Data Selections...');
      history.push(`/${tenant}/pnid_parsing`);
    }
  }, [history, tenant, assetDataKit, fileDataKit]);

  const { items: files } = useSelector(dataKitItemsSelector)(
    filesDataKitId,
    true,
    true
  ) as Result<FilesMetadata>;

  const annotationsByFileId = useSelector(
    (state: RootState) => state.annotations.byFileId
  );
  const partialMatch = useSelector(
    (state: RootState) => state.fileContextualization.pnidOption.partialMatch
  );
  const grayscale = useSelector(
    (state: RootState) => state.fileContextualization.pnidOption.grayscale
  );

  const { parsingJobs, uploadJobs } = useSelector(
    (state: RootState) => state.fileContextualization
  );

  const isLoading = Object.values(uploadJobs).some(job => !job.jobDone);

  const [selectedKeys, setSelectedKeys] = useState([] as number[]);

  useEffect(() => {
    dispatch(
      startPnidParsingPipeline(
        filesDataKitId,
        assetsDataKitId,
        partialMatch,
        grayscale
      )
    );
  }, [dispatch, assetsDataKitId, filesDataKitId, partialMatch, grayscale]);

  const handleMenuClick = (e: ClickParam, file: FilesMetadata) => {
    switch (e.key) {
      case 'link':
        if (canEditFiles(true)) {
          dispatch(linkFileWithAssetsFromAnnotations(file.id));
        }
    }
  };

  const startUploadJob = () => {
    if (canEditFiles(true)) {
      selectedKeys.forEach(key => {
        if (annotationsByFileId[key].annotations) {
          dispatch(
            startConvertFileToSvgJob(key, annotationsByFileId[key].annotations)
          );
        } else {
          const file = files.find(el => el.id === key);
          if (file) {
            message.error(`${file.name} has no annotations`);
          } else {
            message.error(`we are still loading file ${key}`);
          }
        }
      });
    }
  };

  const rows = files
    .filter(el => !!el)
    .map(file => {
      return {
        ...file,
        parsingJob: parsingJobs[file.id],
        uploadJob: uploadJobs[file.id],
      } as FilesMetadata & {
        parsingJob?: ParsingJobState;
        uploadJob?: UploadJobState;
      };
    });

  return (
    <>
      <Row type="flex" align="middle" justify="space-between">
        <Col>
          <h1>P&ID Parsing</h1>
        </Col>
        <Col>
          <Tooltip
            placement="left"
            arrowPointAtCenter
            title="This will create or update an interactive SVG linked to the assets for the selected files."
          >
            <Icon
              type="Help"
              style={{
                marginRight: '24px',
                fontSize: '18px',
                display: 'inline-block',
              }}
            />
          </Tooltip>
          <Button
            type="primary"
            disabled={selectedKeys.length === 0}
            onClick={startUploadJob}
            loading={isLoading}
          >
            {`Deploy ${selectedKeys.length} files to CDF`}
          </Button>
        </Col>
      </Row>
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <LoadResources
            assetDataKitId={assetsDataKitId}
            fileDataKitId={filesDataKitId}
          />
        </Col>
      </Row>
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Table
            rowSelection={{
              onSelectAll: selectAll =>
                setSelectedKeys(
                  selectAll ? rows.map(el => el.id) : ([] as number[])
                ),
              onChange: keys => setSelectedKeys(keys as number[]),
              selectedRowKeys: selectedKeys,
            }}
            columns={[
              { dataIndex: 'name', title: 'Name', key: 'name' },
              {
                title: 'Status',
                key: 'status',
                render: (_, file) => {
                  const { uploadJob, parsingJob } = file;

                  // Error states
                  if (uploadJob && uploadJob.jobError) {
                    return <Badge color="red" text="Failed to Upload" />;
                  }
                  if (parsingJob && parsingJob.jobError) {
                    return <Badge color="red" text="Failed to parse P&ID" />;
                  }

                  // Regular statuses
                  if (uploadJob) {
                    if (uploadJob.jobDone) {
                      return <Badge color="green" text="Completed" />;
                    }
                    return <Badge color="blue" text="Uploading" />;
                  }
                  if (parsingJob) {
                    if (parsingJob.jobDone) {
                      return <Badge color="green" text="Finished Parsing" />;
                    }
                    return <Badge color="yellow" text="Parsing P&ID..." />;
                  }
                  return <Badge color="grey" text="Pending" />;
                },
              },
              {
                title: 'New Linked Tags Detected',
                key: 'annotations_new',
                render: (_, file) => {
                  const { parsingJob } = file;
                  if (!parsingJob || !parsingJob.jobDone) {
                    return <Spin size="small" />;
                  }
                  const annotations = annotationBySourceMap(
                    file.id,
                    `job:${parsingJob.jobId}`
                  );

                  const newAssets = annotations.filter(
                    el => el.resourceType === 'asset'
                  ).length;
                  const newFiles = annotations.filter(
                    el => el.resourceType === 'file'
                  ).length;

                  const leftOver = annotations.length - newAssets - newFiles;

                  return (
                    <Popover
                      title="New Linked Tags Detected"
                      placement="bottomLeft"
                      content={
                        <>
                          <p>
                            <Badge
                              style={{
                                backgroundColor: Colors['midorange-3'].hex(),
                              }}
                              count={newFiles}
                              showZero
                            />{' '}
                            File Tags
                          </p>
                          <p>
                            <Badge
                              style={{
                                backgroundColor: Colors['purple-3'].hex(),
                              }}
                              count={newAssets}
                              showZero
                            />{' '}
                            Asset Tags
                          </p>
                          <p>
                            <Badge
                              style={{
                                backgroundColor: Colors['pink-3'].hex(),
                              }}
                              count={leftOver}
                              showZero
                            />{' '}
                            Unclassified Tags
                          </p>
                        </>
                      }
                    >
                      <span>
                        <Badge
                          style={{ backgroundColor: Colors.midblue.hex() }}
                          count={annotations.length}
                          showZero
                        />{' '}
                        New Tags
                      </span>
                    </Popover>
                  );
                },
              },
              {
                title: 'Total Linked Tags',
                key: 'annotations',
                render: (file: FilesMetadata) => {
                  const annotations = annotationsMap(file.id);
                  if (!annotations) {
                    return <Spin size="small" />;
                  }

                  const annotationDetails = getPnIdAnnotationCategories(
                    annotations
                  );

                  const {
                    Asset: { count: assetCount },
                    File: { count: fileCount },
                    Unclassified: { count: unclassifiedCount },
                  } = annotationDetails;

                  return (
                    <Popover
                      title="Total Detected Linked Tags"
                      placement="bottomLeft"
                      content={
                        <>
                          {Object.keys(annotationDetails).map(key => {
                            const { count, items } = annotationDetails[key];
                            return (
                              <div key={key} style={{ marginBottom: '12px' }}>
                                <strong>
                                  {count} {key} Linked Tags
                                </strong>
                                {Object.keys(items).map(subKey => (
                                  <Row
                                    key={subKey}
                                    type="flex"
                                    align="middle"
                                    style={{ marginBottom: '4px' }}
                                  >
                                    <Col span={16}>{subKey}</Col>
                                    <Col
                                      span={8}
                                      style={{
                                        float: 'right',
                                        display: 'inline-flex',
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <Badge
                                        style={{
                                          backgroundColor: selectAnnotationColor(
                                            items[subKey][0]
                                          ),
                                        }}
                                        count={items[subKey].length}
                                        showZero
                                      />
                                    </Col>
                                  </Row>
                                ))}
                              </div>
                            );
                          })}
                        </>
                      }
                    >
                      <div>
                        <span>
                          <Badge
                            style={{
                              backgroundColor: Colors['purple-3'].hex(),
                            }}
                            count={fileCount}
                            showZero
                          />{' '}
                          File Tags
                        </span>
                        <span style={{ marginLeft: '12px' }}>
                          <Badge
                            style={{
                              backgroundColor: Colors['midorange-3'].hex(),
                            }}
                            count={assetCount}
                            showZero
                          />{' '}
                          Asset Tags
                        </span>
                        <span style={{ marginLeft: '12px' }}>
                          <Badge
                            style={{ backgroundColor: Colors['pink-3'].hex() }}
                            count={unclassifiedCount}
                            showZero
                          />{' '}
                          Unclassified Tags
                        </span>
                      </div>
                    </Popover>
                  );
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                render: file => {
                  return (
                    <>
                      <Button
                        icon="ArrowRight"
                        onClick={() => {
                          if (file) {
                            history.push(
                              `/${tenant}/pnid_parsing/${filesDataKitId}/${assetsDataKitId}/${optionsId}/pnid/${file.id}`
                            );
                          } else {
                            message.info(
                              'Please wait for the process to finish for this file.'
                            );
                          }
                        }}
                        disabled={
                          !(file && file.parsingJob && file.parsingJob.jobDone)
                        }
                      >
                        View
                      </Button>
                      <Dropdown
                        overlay={
                          <Menu onClick={e => handleMenuClick(e, file)}>
                            <Menu.Item key="link">
                              Link Assets to File
                            </Menu.Item>
                          </Menu>
                        }
                      >
                        <Button
                          style={{
                            marginLeft: '8px',
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            width: 'auto',
                            minWidth: 'auto',
                          }}
                          disabled={
                            !(
                              file &&
                              file.annotations &&
                              file.annotations.length > 0
                            )
                          }
                          icon="HorizontalEllipsis"
                        />
                      </Dropdown>
                    </>
                  );
                },
              },
            ]}
            dataSource={rows}
            rowKey="id"
            pagination={{
              onChange: newPage => {
                history.push({
                  search: queryString.stringify({
                    ...queryString.parse(search),
                    page: newPage,
                  }),
                });
              },
              current: (page || 0) as number,
            }}
          />
        </Col>
      </Row>
    </>
  );
}
